/**
 * Marketplace service — MOCK-FIRST (CT-3893).
 *
 * The "what could I add?" catalog: available integrations with source +
 * verification metadata and a per-workspace connection state. No backend yet
 * (CT-3904); same conventions as `integrationsService` — platform envelope,
 * snake_case, module-level mutable seed, `TODO(CT-3904 / backend)` per function.
 */
import { envelope, matchesFilters } from '../shared/utils/mockServiceHelpers.js';

import { listIntegrations, ATTENTION_STATUSES } from './integrationsService.js';

const SEED_CATALOG = [
  // Connected in this workspace (mirror the integrationsService connected set).
  {
    id: 'slack',
    slug: 'slack',
    name: 'Slack',
    vendor: 'Slack Technologies',
    category: 'Communication',
    description: 'Team messaging, alerts and approvals.',
    source: 'official',
    verified: true,
    connection_state: 'connected',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
  {
    id: 'salesforce',
    slug: 'salesforce',
    name: 'Salesforce',
    vendor: 'Salesforce.com',
    category: 'CRM & Sales',
    description: 'Sync accounts, contacts and opportunities.',
    source: 'official',
    verified: true,
    connection_state: 'connected',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
  {
    id: 'github',
    slug: 'github',
    name: 'GitHub',
    vendor: 'GitHub Inc.',
    category: 'Dev & Code',
    description: 'Repos, issues and pull requests.',
    source: 'official',
    verified: true,
    connection_state: 'connected',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
  {
    id: 'notion',
    slug: 'notion',
    name: 'Notion',
    vendor: 'Notion Labs',
    category: 'Productivity & Docs',
    description: 'Docs and databases for your team.',
    source: 'official',
    verified: true,
    connection_state: 'connected',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
  {
    id: 'snowflake',
    slug: 'snowflake',
    name: 'Snowflake',
    vendor: 'Snowflake Inc.',
    category: 'Data & Analytics',
    description: 'Query and sync your data warehouse.',
    source: 'official',
    verified: true,
    connection_state: 'connected',
    featured: true,
    popular: false,
    new_from_partners: false,
    // Editorial fields for the Featured Card (CT-4163).
    featured_headline: 'Turn Snowflake into a Knowledge source for Governance',
    featured_description:
      'Use your data warehouse tables as searchable knowledge for agents, or invoke Snowflake queries as tools from Agentic. SOC 2 compliant and fully auditable.',
    capabilities: [
      { type: 'data_sync', active: true },
      { type: 'tool', active: true },
    ],
  },
  // Curated (addable).
  {
    id: 'hubspot',
    slug: 'hubspot',
    name: 'HubSpot',
    vendor: 'HubSpot Inc.',
    category: 'CRM & Sales',
    description: 'Marketing, sales and service in one platform.',
    source: 'official',
    verified: true,
    connection_state: 'not_connected',
    featured: false,
    popular: true,
    new_from_partners: false,
  },
  {
    id: 'stripe',
    slug: 'stripe',
    name: 'Stripe',
    vendor: 'Stripe Inc.',
    category: 'Data & Analytics',
    description: 'Payments, billing and revenue data.',
    source: 'official',
    verified: true,
    connection_state: 'not_connected',
    featured: false,
    popular: true,
    new_from_partners: false,
  },
  {
    id: 'airtable',
    slug: 'airtable',
    name: 'Airtable',
    vendor: 'Airtable Inc.',
    category: 'Productivity & Docs',
    description: 'Flexible databases and workflows.',
    source: 'official',
    verified: true,
    connection_state: 'not_connected',
    featured: false,
    popular: true,
    new_from_partners: false,
  },
  {
    id: 'segment',
    slug: 'segment',
    name: 'Segment',
    vendor: 'Twilio Segment',
    category: 'Data & Analytics',
    description: 'Customer data pipelines.',
    source: 'official',
    verified: true,
    connection_state: 'not_connected',
    featured: false,
    popular: false,
    new_from_partners: true,
  },
  {
    id: 'pagerduty',
    slug: 'pagerduty',
    name: 'PagerDuty',
    vendor: 'PagerDuty Inc.',
    category: 'Security & Observability',
    description: 'On-call and incident response.',
    source: 'official',
    verified: true,
    connection_state: 'not_connected',
    featured: false,
    popular: false,
    new_from_partners: true,
  },
  {
    id: 'intercom',
    slug: 'intercom',
    name: 'Intercom',
    vendor: 'Intercom Inc.',
    category: 'Communication',
    description: 'Customer messaging and support.',
    source: 'official',
    verified: true,
    connection_state: 'not_connected',
    featured: false,
    popular: false,
    new_from_partners: true,
  },
  {
    id: 'workday',
    slug: 'workday',
    name: 'Workday',
    vendor: 'Acme HR Connect',
    category: 'Productivity & Docs',
    description: 'HCM, payroll and people data through the Acme HR Connect bridge.',
    source: 'partner',
    verified: false,
    connection_state: 'not_connected',
    featured: false,
    popular: false,
    new_from_partners: true,
  },
  {
    id: 'confluence',
    slug: 'confluence',
    name: 'Confluence',
    vendor: 'Atlassian',
    category: 'Productivity & Docs',
    description: 'Team wiki and knowledge base.',
    source: 'official',
    verified: true,
    connection_state: 'in_marketplace',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
  {
    id: 'twilio',
    slug: 'twilio',
    name: 'Twilio',
    vendor: 'Twilio Inc.',
    category: 'Communication',
    description: 'Programmable SMS and voice.',
    source: 'official',
    verified: true,
    connection_state: 'not_connected',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
  {
    id: 'figma',
    slug: 'figma',
    name: 'Figma',
    vendor: 'Figma Inc.',
    category: 'Dev & Code',
    description: 'Design files and comments.',
    source: 'official',
    verified: false,
    connection_state: 'not_connected',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
  {
    id: 'mixpanel',
    slug: 'mixpanel',
    name: 'Mixpanel',
    vendor: 'Mixpanel Inc.',
    category: 'Data & Analytics',
    description: 'Product analytics and funnels.',
    source: 'official',
    verified: true,
    connection_state: 'not_connected',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
  {
    id: 'acme-crm',
    slug: 'acme-crm',
    name: 'Acme CRM',
    vendor: 'Acme (community)',
    category: 'CRM & Sales',
    description: 'Community-built connector for Acme.',
    source: 'private',
    verified: false,
    connection_state: 'in_marketplace',
    featured: false,
    popular: false,
    new_from_partners: false,
  },
];

/**
 * Filter facets the Filters slideout (CT-4168) queries, keyed by slug. Kept as a
 * config map (not inlined per row) so adding a capability/studio to a connector
 * is a one-line edit. `capability_types` are the active capability slugs (Tool /
 * Data Sync / MCP); `used_in_studio` are the AIMS studios that consume it.
 * TODO(CT-3904 / backend): these arrive on the real marketplace payload.
 */
const CAPABILITY_TYPES_BY_SLUG = {
  slack: ['tool', 'data_sync'],
  salesforce: ['data_sync', 'tool'],
  github: ['tool', 'mcp'],
  notion: ['data_sync', 'mcp'],
  snowflake: ['data_sync', 'tool'],
  hubspot: ['data_sync', 'tool'],
  stripe: ['tool'],
  airtable: ['data_sync'],
  segment: ['data_sync'],
  pagerduty: ['tool'],
  intercom: ['tool', 'data_sync'],
  workday: ['data_sync'],
  confluence: ['data_sync', 'mcp'],
  twilio: ['tool'],
  figma: ['mcp'],
  mixpanel: ['data_sync'],
  'acme-crm': ['tool'],
};

const STUDIOS_BY_SLUG = {
  slack: ['workforce', 'agentic'],
  salesforce: ['agentic', 'governance'],
  github: ['agentic'],
  notion: ['governance'],
  snowflake: ['governance', 'agentic'],
  hubspot: ['agentic'],
  stripe: ['agentic'],
  airtable: ['governance'],
  segment: ['governance'],
  pagerduty: ['workforce'],
  intercom: ['workforce'],
  workday: ['governance'],
  confluence: ['governance'],
  twilio: ['workforce'],
  figma: ['agentic'],
  mixpanel: ['agentic'],
  'acme-crm': ['agentic'],
};

/**
 * Fresh, mutable seed with the CT-4168 filter facets attached. Returns new
 * top-level rows on each call so `__resetMockMarketplaceForTests` truly resets.
 */
const buildSeedCatalog = () =>
  SEED_CATALOG.map((item) => ({
    ...item,
    capability_types: CAPABILITY_TYPES_BY_SLUG[item.slug] ?? [],
    used_in_studio: STUDIOS_BY_SLUG[item.slug] ?? [],
  }));

let mockCatalog = buildSeedCatalog();

/** Test-only: reset the mutable seed. */
export const __resetMockMarketplaceForTests = () => {
  mockCatalog = buildSeedCatalog();
};

const matchesSearch = (item, query) => {
  const q = (query ?? '').trim().toLowerCase();
  if (!q) return true;
  return `${item.name} ${item.category}`.toLowerCase().includes(q);
};

/**
 * Full marketplace catalog, searched/filtered/sorted/paginated (Browse All).
 * TODO(CT-3904 / backend): replace with `POST /v1/workspace/integrations/marketplace/list`.
 */
export const listMarketplace = (params = {}) => {
  const {
    page = 1,
    page_size = 12,
    order_by = 'name',
    order_direction = 'asc',
    filters = {},
    search = {},
  } = params;

  const filtered = mockCatalog
    .filter((i) => matchesFilters(i, filters))
    .filter((i) => matchesSearch(i, search.query));

  const sorted = [...filtered].sort((a, b) => {
    const cmp = String(a[order_by] ?? '').localeCompare(String(b[order_by] ?? ''));
    return order_direction === 'desc' ? -cmp : cmp;
  });

  const total = sorted.length;
  const start = (page - 1) * page_size;
  const items = sorted.slice(start, start + page_size);
  return Promise.resolve(
    envelope(items, { total, page, page_size, total_pages: Math.max(1, Math.ceil(total / page_size)) }),
  );
};

/**
 * Curated marketplace payload: active summary, featured card, curated grids.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/marketplace/curated`.
 *
 * The Active Strip mirrors the real connected set from `integrationsService` (the
 * same source as the "Already Integrated N" tab badge) so the count, the health
 * dots, and the "N need attention" note stay consistent across the home — 13
 * connected / 8 needing attention in the mock — instead of a divergent stub.
 */
export const getMarketplaceCurated = async () => {
  const { body: connected } = await listIntegrations({ filters: { connected: true }, page_size: 100 });
  return envelope({
    active_summary: {
      connected_count: connected.length,
      needs_attention_count: connected.filter((i) => ATTENTION_STATUSES.has(i.status)).length,
      // DEMO-ONLY (CT-3904 backend): static "minutes since last sync" for the strip subtitle.
      last_update_minutes: 2,
      top: connected.slice(0, 5).map((i) => ({ slug: i.slug, name: i.name, status: i.status })),
    },
    featured: mockCatalog.find((i) => i.featured) ?? null,
    curated: {
      popular: mockCatalog.filter((i) => i.popular),
      new_from_partners: mockCatalog.filter((i) => i.new_from_partners),
    },
    total_count: mockCatalog.length,
  });
};

/**
 * Catalog counts for the Marketplace hero (CT-4993).
 * TODO(CT-3904 / backend): replace with stats on the curated/list envelope.
 */
export const getMarketplaceHeroStats = async () =>
  envelope({
    catalog_count: mockCatalog.length,
    categories_count: new Set(mockCatalog.map((i) => i.category)).size,
    official_count: mockCatalog.filter((i) => i.source === 'official').length,
    partner_count: mockCatalog.filter((i) => i.source === 'partner').length,
    private_count: mockCatalog.filter((i) => i.source === 'private' || i.source === 'unofficial').length,
  });

/** Distinct filter facets for the Filters slideout (CT-4168). */
export const getMarketplaceFacets = () =>
  Promise.resolve(
    envelope({
      categories: [...new Set(mockCatalog.map((i) => i.category))].sort(),
      capability_types: ['tool', 'data_sync', 'mcp'],
      connection_states: ['connected', 'not_connected'],
      used_in_studio: ['governance', 'agentic', 'workforce'],
      sources: ['official', 'partner', 'private'],
    }),
  );
